import React, { useState } from 'react';
import { MessageSquare, Send, User, Flower, Quote, CornerDownRight, ShieldCheck, Star, Lock } from 'lucide-react';
import { Comment, Language } from '../types';

interface CommentsSectionProps {
  comments: Comment[];
  onAddComment: (text: string, rating: number) => void;
  currentUser: { name: string; email: string } | null;
  language: Language;
  onOpenAuth: () => void;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({ comments, onAddComment, currentUser, language, onOpenAuth }) => {
  const [inputText, setInputText] = useState('');
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState(false);

  const t = {
    pt: {
      title: 'Avaliações do Perfil',
      sub: 'Relatórios e Depoimentos dos Clientes',
      placeholder: 'Fale sobre a qualidade das artes, animação ou velocidade na entrega...',
      btn: 'Enviar Avaliação',
      loginRequiredText: 'Apenas clientes cadastrados e logados podem deixar uma avaliação.',
      loginBtn: 'Entrar / Cadastrar-se',
      empty: 'Ainda não há depoimentos. Compartilhe sua experiência primeiro!',
      stats: 'Reputação do Clã',
      masterReply: 'Resposta do Mestre',
      averageRating: 'Média de Satisfação',
      votes: 'depoimentos',
      writeReview: 'Fazer uma Avaliação',
      starRating: 'Sua Nota',
      yourName: 'Seu Nome / Nickname',
      yourEmail: 'Seu E-mail (opcional)',
      successAlert: 'Seu depoimento foi selado com sucesso!',
      verifiedPurchase: 'Colecionador Verificado',
      anonymous: 'Anônimo',
      ratingsBreakdown: 'Distribuição de Notas'
    },
    en: {
      title: 'Client Reviews',
      sub: 'Collector Testimonials & Feedback',
      placeholder: 'Share your thoughts on art quality, animation smoothness, or speed...',
      btn: 'Submit Testimonial',
      loginRequiredText: 'Only registered and logged-in clients can submit reviews.',
      loginBtn: 'Login / Register',
      empty: 'No testimonials left yet. Be the first to seal your comment!',
      stats: 'Clan Reputation',
      masterReply: 'Artist\'s Reply',
      averageRating: 'Average Satisfaction',
      votes: 'testimonials',
      writeReview: 'Write a Review',
      starRating: 'Your Rating',
      yourName: 'Your Name / Nickname',
      yourEmail: 'Your Email (optional)',
      successAlert: 'Your feedback has been successfully sealed!',
      verifiedPurchase: 'Verified Collector',
      anonymous: 'Anonymous',
      ratingsBreakdown: 'Ratings Breakdown'
    },
    es: {
      title: 'Reseñas de Clientes',
      sub: 'Testimonios y Comentarios de Coleccionistas',
      placeholder: 'Comparte tu opinión sobre la calidad, fluidez de animación o rapidez...',
      btn: 'Enviar Testimonio',
      loginRequiredText: 'Solo los clientes registrados y autenticados pueden dejar reseñas.',
      loginBtn: 'Iniciar Sesión / Registrarse',
      empty: 'Aún no hay testimonios. ¡Sé el primero en dejar tu comentario!',
      stats: 'Reputación del Clan',
      masterReply: 'Respuesta del Artista',
      averageRating: 'Satisfacción Media',
      votes: 'testimonios',
      writeReview: 'Escribir una Reseña',
      starRating: 'Tu Calificación',
      yourName: 'Tu Nombre / Nickname',
      yourEmail: 'Tu Correo (opcional)',
      successAlert: '¡Tu testimonio ha sido registrado con éxito!',
      verifiedPurchase: 'Coleccionista Verificado',
      anonymous: 'Anónimo',
      ratingsBreakdown: 'Distribución de Calificaciones'
    },
    ru: {
      title: 'Отзывы Клиентов',
      sub: 'Отзывы и Впечатления Коллекционеров',
      placeholder: 'Поделитесь впечатлениями о качестве арта, плавности анимации или скорости...',
      btn: 'Отправить Отзыв',
      loginRequiredText: 'Только зарегистрированные пользователи могут оставлять отзывы.',
      loginBtn: 'Войти / Регистрация',
      empty: 'Отзывов пока нет. Будьте первым, кто поделится мнением!',
      stats: 'Репутация Клана',
      masterReply: 'Ответ Мастера',
      averageRating: 'Средний Рейтинг',
      votes: 'отзывов',
      writeReview: 'Написать Отзыв',
      starRating: 'Ваша Оценка',
      yourName: 'Ваше Имя / Никнейм',
      yourEmail: 'Ваш Email (необязательно)',
      successAlert: 'Ваш отзыв успешно опубликован!',
      verifiedPurchase: 'Проверенный Покупатель',
      anonymous: 'Аноним',
      ratingsBreakdown: 'Распределение Оценок'
    }
  }[language] || {
    title: 'Client Reviews',
    sub: 'Collector Testimonials & Feedback',
    placeholder: 'Share your thoughts on art quality, animation smoothness, or speed...',
    btn: 'Submit Testimonial',
    loginRequiredText: 'Only registered and logged-in clients can submit reviews.',
    loginBtn: 'Login / Register',
    empty: 'No testimonials left yet. Be the first to seal your comment!',
    stats: 'Clan Reputation',
    masterReply: 'Artist\'s Reply',
    averageRating: 'Average Satisfaction',
    votes: 'testimonials',
    writeReview: 'Write a Review',
    starRating: 'Your Rating',
    yourName: 'Your Name / Nickname',
    yourEmail: 'Your Email (optional)',
    successAlert: 'Your feedback has been successfully sealed!',
    verifiedPurchase: 'Verified Collector',
    anonymous: 'Anonymous',
    ratingsBreakdown: 'Ratings Breakdown'
  };

  // Proteção contra dados não-array, remoção de SEEDs e de-duplicação
  const safeComments = Array.isArray(comments)
    ? comments
        .filter(c => c && c.id && !c.id.startsWith('SEED-'))
        .filter((comment, index, self) => self.findIndex(c => c.id === comment.id) === index)
    : [];

  // Calcular estatísticas de avaliações
  const totalReviews = safeComments.length;
  const averageValue = totalReviews > 0 
    ? (safeComments.reduce((acc, curr) => acc + (curr.rating || 5), 0) / totalReviews).toFixed(1)
    : '5.0';

  // Contagem por estrela (1 a 5)
  const starCounts = [0, 0, 0, 0, 0];
  safeComments.forEach(c => {
    const r = c.rating || 5;
    const idx = Math.max(1, Math.min(5, Math.round(r))) - 1;
    starCounts[idx]++;
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    if (!currentUser) {
      return;
    }

    onAddComment(inputText, rating);
    
    // Resetar campos
    setInputText('');
    setRating(5);
    
    // Mostrar animação de sucesso
    setSuccessMsg(true);
    setTimeout(() => {
      setSuccessMsg(false);
    }, 4500);
  };

  return (
    <div className="w-full space-y-16 py-10" id="reviews-section">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 mb-16">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white border-b-4 border-[#c084fc] shadow-xl">
              <MessageSquare className="w-6 h-6 text-[#c084fc]" />
            </div>
            <span className="text-[#c084fc] font-black text-xs tracking-[0.5em] uppercase">{t.stats}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter uppercase">{t.title}</h2>
          <p className="text-zinc-500 font-bold uppercase text-[10px] tracking-widest mt-2">{t.sub}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Lado Esquerdo: Estatísticas e Form de Postagem */}
        <div className="lg:col-span-5 space-y-10">
          
          {/* Card de Resumo das Avaliações */}
          <div className="bg-zinc-950 border border-white/5 p-8 relative">
            <div className="absolute top-0 right-10 w-2 h-12 bg-[#c084fc]" />
            
            <h3 className="text-xs font-black text-zinc-500 uppercase tracking-[0.2em] mb-6">{t.averageRating}</h3>
            
            <div className="flex items-baseline gap-4 mb-8">
              <span className="text-6xl font-black text-white leading-none tracking-tighter">{averageValue}</span>
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star 
                      key={s} 
                      className={`w-4 h-4 ${s <= Math.round(parseFloat(averageValue)) ? 'text-[#c084fc] fill-[#c084fc]' : 'text-zinc-800'}`} 
                    />
                  ))}
                </div>
                <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest">
                  {totalReviews} {t.votes}
                </p>
              </div>
            </div>

            {/* Barras de Estatísticas */}
            <div className="space-y-4 pt-4 border-t border-white/5">
              <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2">{t.ratingsBreakdown}</h4>
              {[5, 4, 3, 2, 1].map((sAt) => {
                const count = starCounts[sAt - 1];
                const percent = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                return (
                  <div key={sAt} className="flex items-center gap-3">
                    <span className="text-[10px] text-zinc-500 font-black font-mono w-4">{sAt}★</span>
                    <div className="flex-1 h-2 bg-black border border-white/5 overflow-hidden">
                      <div 
                        className="h-full bg-[#c084fc]" 
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-zinc-400 font-bold font-mono w-6 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form para Avaliar */}
          <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/5 p-8 space-y-6">
            <div>
              <Quote className="w-10 h-10 text-[#c084fc] opacity-20 mb-3" />
              <h3 className="text-lg font-black text-white uppercase tracking-tight">{t.writeReview}</h3>
              <div className="h-0.5 w-12 bg-[#c084fc] mt-2" />
            </div>

            {successMsg && (
              <div className="bg-[#c084fc]/10 border border-[#c084fc]/30 p-4 text-center">
                <p className="text-[#c084fc] text-[10px] font-black uppercase tracking-widest leading-relaxed">
                  ✦ {t.successAlert} ✦
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {!currentUser ? (
                <div className="space-y-6 pt-4 text-center">
                  <div className="inline-flex p-4 rounded-full bg-white/5 border border-white/10 mb-2">
                    <Lock className="w-8 h-8 text-[#c084fc]" />
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed uppercase font-bold tracking-wider max-w-sm mx-auto">
                    {t.loginRequiredText}
                  </p>
                  <button
                    type="button"
                    onClick={onOpenAuth}
                    className="w-full bg-[#c084fc] hover:bg-[#ff82a0] text-white font-black py-4 flex items-center justify-center gap-4 transition-all shadow-2xl uppercase tracking-[0.2em] text-[10px]"
                  >
                    <User className="w-4 h-4" />
                    {t.loginBtn}
                  </button>
                </div>
              ) : (
                <>
                  {/* Estrelas */}
                  <div className="space-y-2 mb-4">
                    <label className="text-[8px] font-black text-zinc-500 uppercase tracking-[0.2em] block">
                      {t.starRating}
                    </label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => {
                        const isFilled = hoverRating !== null ? star <= hoverRating : star <= rating;
                        return (
                          <button
                            type="button"
                            key={star}
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(null)}
                            className="p-1 focus:outline-none focus:scale-110 transition-transform"
                          >
                            <Star 
                              className={`w-6 h-6 transition-all ${isFilled ? 'text-[#c084fc] fill-[#c084fc] drop-shadow-[0_0_8px_#c084fc]' : 'text-zinc-800 hover:text-zinc-500'}`} 
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="p-4 bg-black/40 border border-[#c084fc]/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#c084fc]" />
                      <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">
                        {language === 'pt' ? 'Logado como:' : 'Logged as:'} <span className="text-white font-black">{currentUser.name}</span>
                      </span>
                    </div>
                    <ShieldCheck className="w-4 h-4 text-[#c084fc]" />
                  </div>

                  <div className="space-y-2">
                    <textarea
                      value={inputText}
                      required
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder={t.placeholder}
                      className="w-full bg-black border border-zinc-800 p-4 text-white text-xs placeholder-zinc-700 outline-none focus:border-[#c084fc] min-h-[120px] transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={!inputText.trim()}
                    className="w-full bg-white hover:bg-[#c084fc] text-black hover:text-white font-black py-4 flex items-center justify-center gap-4 transition-all shadow-2xl disabled:opacity-20 uppercase tracking-[0.2em] text-[10px]"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {t.btn}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>

        {/* Lado Direito: Stream de Avaliações */}
        <div className="lg:col-span-7 space-y-8 max-h-[850px] overflow-y-auto pr-4 custom-scrollbar">
          {safeComments.length === 0 ? (
            <div className="py-24 text-center bg-zinc-950/40 border border-zinc-900 flex flex-col items-center justify-center">
              <Flower className="w-12 h-12 text-zinc-800 mb-6 opacity-20" />
              <p className="text-zinc-600 font-black uppercase tracking-widest text-[10px]">{t.empty}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {safeComments.map((comment) => (
                <div key={comment.id} className="space-y-3 animate-in fade-in duration-500">
                  <div className="bg-zinc-900/20 border border-white/5 p-6 relative group hover:border-[#c084fc]/20 transition-all">
                    
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-zinc-950 border border-zinc-800 flex items-center justify-center relative">
                          <User className="w-4 h-4 text-zinc-600" />
                          <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 shadow-[0_0_8px_white] ${comment.verified ? 'bg-[#c084fc]' : 'bg-zinc-500'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-black uppercase text-xs tracking-widest">{comment.userName}</h4>
                            {comment.verified && (
                              <span className="flex items-center gap-1 text-[#c084fc] text-[7px] font-black uppercase bg-[#c084fc]/10 px-2 py-0.5 rounded-full">
                                <ShieldCheck className="w-2.5 h-2.5" />
                                {t.verifiedPurchase}
                              </span>
                            )}
                          </div>
                          <span className="text-zinc-600 text-[8px] font-black uppercase tracking-widest font-mono">{comment.date}</span>
                        </div>
                      </div>

                      {/* Estrelas */}
                      <div className="flex items-center gap-0.5 bg-black/40 px-3 py-1.5 border border-white/5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star 
                            key={s} 
                            className={`w-3.5 h-3.5 ${s <= (comment.rating || 5) ? 'text-[#c084fc] fill-[#c084fc]' : 'text-zinc-800'}`} 
                          />
                        ))}
                      </div>
                    </div>

                    <p className="text-zinc-400 text-[13px] leading-relaxed italic border-l border-zinc-800/80 pl-4 py-1">
                      "{comment.text}"
                    </p>
                  </div>

                  {comment.reply && (
                    <div className="ml-10 md:ml-16 flex gap-4">
                      <div className="mt-2 text-zinc-800">
                        <CornerDownRight className="w-5 h-5" />
                      </div>
                      <div className="flex-1 bg-zinc-950/60 border border-white/5 p-5 relative">
                        <div className="absolute top-0 right-6 w-2 h-2 bg-[#c084fc]" />
                        <div className="flex items-center gap-3 mb-2">
                          <ShieldCheck className="w-3.5 h-3.5 text-[#c084fc]" />
                          <span className="text-[#c084fc] font-black uppercase text-[8px] tracking-[0.2em]">{t.masterReply}</span>
                        </div>
                        <p className="text-zinc-400 text-[12px] leading-relaxed">
                          {comment.reply}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentsSection;
